import { createRoot } from "react-dom/client";
import Player from "./Player";
import RecordingsView from "./RecordingsView";

import videoJsCss from "video.js/dist/video-js.css?inline";
import chromecastCss from "@silvermine/videojs-chromecast/dist/silvermine-videojs-chromecast.css?inline";
import airplayCss from "@silvermine/videojs-airplay/dist/silvermine-videojs-airplay.css?inline";
import themeCss from "./theme.css?inline";

// Kajabi's Custom HTML block strips <link>/<style> tags from pasted markup but
// leaves <script> untouched, so every stylesheet this widget needs is injected
// here at runtime instead of relying on a <link> tag in the embed snippet.
const style = document.createElement("style");
style.textContent = [videoJsCss, chromecastCss, airplayCss, themeCss].join("\n");
document.head.appendChild(style);

// One script, one bundle, both surfaces — the live class lesson and the
// recordings lesson load the exact same embed.js; which view renders is
// decided here, not by shipping two separate builds.
//
// Embedded (Kajabi Custom HTML block): the recordings lesson's snippet sets
// data-mode="recordings" on the mount div; anything else (or no attribute)
// means the live class view, matching every existing embed already pasted.
//
// Standalone (cika-player.pages.dev/?t=...): reached via a handoff link
// minted by either lesson, so the mount div carries no mode hint — instead,
// peek (without verifying — the worker's own /verify call is still the
// trusted check) at the token's own payload to see whether it was minted
// for a specific recording (videoUid set) or the live class (videoUid null).
function decodePayloadNoVerify(token: string): Record<string, unknown> | null {
  try {
    // Our own signed token (see handoff-worker's signToken/verifyToken) is
    // `base64url(payload).base64url(signature)` — two parts, payload first —
    // NOT a 3-segment header.payload.signature JWT.
    const [payloadB64] = token.split(".");
    const json = decodeURIComponent(
      atob(payloadB64.replace(/-/g, "+").replace(/_/g, "/"))
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

const ROOT_ID = "cf-class-player-root";
let mount = document.getElementById(ROOT_ID);
const scriptMode = document.currentScript?.getAttribute("data-mode") ?? mount?.dataset.mode;
if (!mount) {
  mount = document.createElement("div");
  mount.id = ROOT_ID;
  document.currentScript?.parentNode?.insertBefore(mount, document.currentScript);
}

const searchParams = new URLSearchParams(window.location.search);
const tFromUrl = searchParams.get("t");
const peekedVideoUid = tFromUrl ? decodePayloadNoVerify(tFromUrl)?.videoUid : undefined;
// Three ways to land in recordings mode: an embedded data-mode attribute, a
// token whose own payload was minted for a specific recording, or — for a
// fallback link used before any token exists yet (STANDALONE_RECORDINGS_URL,
// see identity.ts) — an explicit ?mode= query param.
const isRecordings = tFromUrl
  ? !!peekedVideoUid
  : scriptMode === "recordings" || searchParams.get("mode") === "recordings";

createRoot(mount).render(isRecordings ? <RecordingsView /> : <Player />);

// Kajabi's Android app injects a native bridge, window.Android, with one
// confirmed real method: onContentHeightChanged(height). That's what the
// native WebView uses to size itself to fit page content — Kajabi's own
// page-side script normally calls it via its own measurement logic
// (measureAndReportHeight/etc.), but poking those functions directly
// (tried previously) was never confirmed to actually fix clipped/
// unscrollable content. Calling the native bridge directly ourselves, with
// our own measurement of the full page height, removes that guesswork —
// it's the one method we know for certain exists and does something.
function reportHeight() {
  const android = (window as unknown as { Android?: { onContentHeightChanged?: (h: number) => void } }).Android;
  if (!android?.onContentHeightChanged) return;
  try {
    // Full document height, not just our own widget — other content on the
    // native page (above/below the embed) needs to stay accounted for too.
    const height = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight
    );
    android.onContentHeightChanged(height);
  } catch {
    // best-effort against an undocumented host-app bridge — ignore failures
  }
}

if (typeof ResizeObserver !== "undefined") {
  // Observe <body>, not just our own mount point — our player's height
  // changes (video.js init, overlay swaps, the live video appearing) all
  // change body's total height too, and this also catches size changes
  // from anything else on the native page.
  new ResizeObserver(() => reportHeight()).observe(document.body);
}
// Also report on a short delay chain — video.js finishes initializing
// async, and ResizeObserver's first callback can lag slightly behind the
// very first paint.
reportHeight();
setTimeout(reportHeight, 500);
setTimeout(reportHeight, 2000);
